import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Alert, Box, CircularProgress, IconButton, MenuItem, Typography } from '@mui/material';
import React from 'react';
import {
  allModelOptions,
  formatModelDisplayName,
  getDropdownSx,
} from '../../../utils/ModelOptions';
import { StyledButton, StyledButtonGroup } from '../../styles/StyledButtons';
import { StyledInput } from '../../styles/StyledInputs';
import { StyledFieldRow, StyledLabel } from '../../styles/styles';
import ArticleRefinementHelpModal from './ArticleRefinementHelpModal';
import { ArticleRefinementChatPanelProps } from './articleApprovalTypes';

export default function ArticleRefinementChatPanel({
  chatWindowCollapsed,
  onToggleChatWindow,
  isProcessing,
  hasRunningJob,
  runningJob,
  chatHistory,
  selectedModel,
  onSelectedModelChange,
  chatInputValue,
  onChatInputChange,
  helpModalOpen,
  onHelpModalOpen,
  onHelpModalClose,
  onRefinementSubmit,
  chatMessagesRef,
  expandedChatHeight,
}: ArticleRefinementChatPanelProps) {
  const dropdownSx = getDropdownSx();

  const handleSend = () => {
    const input = chatInputValue.trim();
    if (input) {
      void onRefinementSubmit(input);
      onChatInputChange('');
    }
  };

  const handleRegenerate = () => {
    void onRefinementSubmit(null, true);
    onChatInputChange('');
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: '3.75rem',
          right: 0,
          height: chatWindowCollapsed ? '60px' : `${expandedChatHeight}px`,
          backgroundColor: '#fff',
          borderTop: '1px solid #e0e0e0',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          transition: 'height 0.3s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            borderBottom: chatWindowCollapsed ? 'none' : '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
          }}
          onClick={onToggleChatWindow}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {chatWindowCollapsed ? 'Refinement Chat' : 'Submit a Refinement Request'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!chatWindowCollapsed && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onHelpModalOpen();
                }}
                size="small"
                sx={{ color: '#078282' }}
              >
                <HelpOutlineIcon />
              </IconButton>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onToggleChatWindow();
              }}
              size="small"
              sx={{ color: '#078282' }}
            >
              {chatWindowCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Box>
        </Box>

        {chatWindowCollapsed && (isProcessing || hasRunningJob) && (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: '#e3f2fd',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <CircularProgress size={16} />
          </Box>
        )}

        {!chatWindowCollapsed && (
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <StyledButtonGroup justifyContent="flex-start">
              <StyledButton
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleRegenerate}
                disabled={isProcessing || hasRunningJob}
              >
                Regenerate Entire Article
              </StyledButton>
            </StyledButtonGroup>
          </Box>
        )}

        {!chatWindowCollapsed && (
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <StyledFieldRow stacked sx={{ mb: 0 }}>
              <StyledLabel stacked sx={{ fontSize: '0.875rem' }}>
                AI Model for Refinement
              </StyledLabel>
              <StyledInput
                select
                sx={{ ...dropdownSx, fontSize: '0.875rem' }}
                value={selectedModel}
                onChange={(e) => onSelectedModelChange(e.target.value)}
                disabled={isProcessing || hasRunningJob}
              >
                {allModelOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {formatModelDisplayName(opt)}
                  </MenuItem>
                ))}
              </StyledInput>
            </StyledFieldRow>
          </Box>
        )}

        {!chatWindowCollapsed && hasRunningJob && runningJob && (
          <Alert severity="info" sx={{ m: 1.5, mb: 1, py: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              <strong>Refinement in Progress:</strong> {runningJob.progressText || runningJob.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              The chat window is disabled while the refinement job is running. You can navigate away
              and return later - the job will continue in the background.
            </Typography>
          </Alert>
        )}

        {!chatWindowCollapsed && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {hasRunningJob ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body1" component="span">
                  Refinement job is running... Please wait.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                <Box
                  ref={chatMessagesRef}
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    maxHeight: '150px',
                    minHeight: '80px',
                    p: 1.5,
                  }}
                >
                  {chatHistory.map((msg) => (
                    <Box
                      key={msg.timestamp.toISOString()}
                      sx={{
                        p: 1,
                        mb: 1,
                        maxWidth: '85%',
                        ml: msg.role === 'user' ? 'auto' : '0',
                        mr: msg.role === 'model' ? 'auto' : '0',
                        bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                        color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                        borderRadius:
                          msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        wordWrap: 'break-word',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {msg.parts.map((p) => p.text).join('\n\n')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: msg.role === 'user' ? 'right' : 'left',
                          fontSize: '0.65rem',
                          opacity: 0.75,
                          mt: 0.5,
                        }}
                      >
                        {msg.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  ))}
                  {isProcessing && (
                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-start' }}>
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: 'grey.100',
                          borderRadius: '18px 18px 18px 4px',
                          maxWidth: '65px',
                          height: '24px',
                          display: 'flex',
                          justifyContent: 'space-around',
                          alignItems: 'center',
                        }}
                      >
                        {[0, 1, 2].map((i) => (
                          <Box
                            key={i}
                            sx={{
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              bgcolor: 'text.secondary',
                              animation: 'blink 1.4s infinite',
                              animationDelay: `${i * 0.2}s`,
                              '@keyframes blink': {
                                '0%, 80%, 100%': { opacity: 0 },
                                '40%': { opacity: 1 },
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StyledInput
              fullWidth
              variant="outlined"
              size="small"
              placeholder={
                chatWindowCollapsed
                  ? 'Type refinement request...'
                  : 'Suggest a refinement to improve this article...'
              }
              value={chatInputValue}
              onChange={(e) => onChatInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isProcessing) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={isProcessing}
            />
            <StyledButton
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSend}
              disabled={isProcessing}
            >
              Send
            </StyledButton>
          </Box>
        </Box>
      </Box>

      <ArticleRefinementHelpModal open={helpModalOpen} onClose={onHelpModalClose} />
    </>
  );
}
